import { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { useNavigate, useLocation, Navigate } from 'react-router-dom';
import { useAuth } from '@/contexts/auth/AuthContext';
import { MinimalAppSkeleton } from '@/components/ui/app-skeleton';
import CustomerSelectionHeader from '@/components/customers/CustomerSelectionHeader';
import CustomerSelectionGrid from '@/components/customers/CustomerSelectionGrid';
import CustomerErrorState from '@/components/customers/CustomerErrorState';
import NoCustomersState from '@/components/customers/NoCustomersState';
import { createScopedLogger } from '@/utils/debug';

export const CustomerRedirect = () => {
  const { 
    isAuthenticated, 
    isInitialized, 
    isLoading,
    user,
    customers, 
    activeCustomer,
    selectCustomer,
    logout
  } = useAuth();
  
  const navigate = useNavigate();
  const location = useLocation();
  const [showSelectionUI, setShowSelectionUI] = useState(false);
  const [customerSelectError, setCustomerSelectError] = useState<string | null>(null);
  const [isLoadingCustomer, setIsLoadingCustomer] = useState(false);
  const [loadingCustomerId, setLoadingCustomerId] = useState<string | null>(null);
  const autoSelectionAttemptedRef = useRef(false);

  // Get the intended destination from location state or default to dashboard
  const from = location.state?.from || '/dashboard';

  // Debug log function that only logs for superusers
  const logger = useCallback(() => createScopedLogger(user, 'CustomerRedirect'), [user]);
  
  const logDebug = useCallback((message: string, data?: any) => {
    logger().debug(message, data);
  }, [logger]);

  // Helper function to get the last active customer ID from storage
  const getLastActiveCustomerId = useCallback(() => {
    try {
      return localStorage.getItem('lastActiveCustomerId');
    } catch (error) {
      console.warn('Failed to get last active customer ID from localStorage:', error);
      return null;
    }
  }, []);

  // Helper function to set the last active customer ID in storage
  const setLastActiveCustomerId = useCallback((customerId: string) => {
    try {
      localStorage.setItem('lastActiveCustomerId', customerId);
    } catch (error) {
      console.warn('Failed to set last active customer ID in localStorage:', error);
    }
  }, []);

  // Auto-selection logic
  useEffect(() => {
    const handleSelection = async () => {
      // Don't proceed if we're still initializing, loading, or not authenticated
      if (autoSelectionAttemptedRef.current || !isInitialized || isLoading || !isAuthenticated) {
        return;
      }

      // Mark as attempted immediately to prevent duplicate runs
      autoSelectionAttemptedRef.current = true;

      try {
        logDebug('Starting customer auto-selection process');
        
        // If user already has an active customer, redirect to intended destination
        if (activeCustomer) {
          logDebug(`User already has active customer (${activeCustomer.name}), redirecting to ${from}`);
          navigate(from, { replace: true });
          return;
        }

        // If no customers available, show error
        if (!customers || customers.length === 0) {
          logDebug('No customers available for selection');
          setShowSelectionUI(true);
          return;
        }

        // If only one customer, auto-select it
        if (customers.length === 1) {
          logDebug(`Only one customer available (${customers[0].name}), auto-selecting`);
          const result = await selectCustomer(customers[0]);
          
          if (result.success) {
            logDebug('Single customer auto-selection successful');
            setLastActiveCustomerId(String(customers[0].id));
            navigate(from, { replace: true });
            return;
          } else {
            logDebug(`Single customer auto-selection failed: ${result.error}`);
            setCustomerSelectError(result.error || 'Failed to select customer');
            setShowSelectionUI(true);
            return;
          }
        }

        // Try to use last active customer from storage
        const lastActiveCustomerId = getLastActiveCustomerId();
        
        if (lastActiveCustomerId && customers.length > 1) {
          // Find the last active customer by ID
          const lastActiveCustomer = customers.find(c => 
            String(c.id) === String(lastActiveCustomerId)
          );
          
          if (lastActiveCustomer) {
            logDebug(`Found last active customer (${lastActiveCustomer.name}), selecting it`);
            const result = await selectCustomer(lastActiveCustomer);
            
            if (result.success) {
              logDebug('Last active customer selection successful');
              navigate(from, { replace: true });
              return;
            } else {
              logDebug(`Last active customer selection failed: ${result.error}`);
              setCustomerSelectError(result.error || 'Failed to select last active customer');
            }
          } else {
            logDebug(`Last active customer ID ${lastActiveCustomerId} not found in available customers`);
            logDebug('Available customer IDs:', customers.map(c => ({ id: c.id, name: c.name })));
          }
        }
        
        // If we get here, we need to show the selection UI
        logDebug('Auto-selection not possible, showing customer selection UI');
        setShowSelectionUI(true);
      } catch (error) {
        console.error('Error in auto-selection:', error);
        setCustomerSelectError('Error during automatic customer selection');
        setShowSelectionUI(true);
      }
    };
    
    // Start the auto-selection process
    handleSelection();
  }, [
    isInitialized, 
    isLoading, 
    isAuthenticated, 
    activeCustomer, 
    user, 
    customers, 
    selectCustomer, 
    navigate, 
    from, 
    logDebug,
    getLastActiveCustomerId
  ]);

  // Redirect to login if not authenticated (after initialization)
  if (isInitialized && !isLoading && !isAuthenticated) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }

  // Show skeleton loading while initializing or processing
  if (!showSelectionUI) {
    return <MinimalAppSkeleton />;
  }

  // Handle customer selection
  const handleCustomerSelect = async (customer) => {
    setIsLoadingCustomer(true);
    setLoadingCustomerId(customer.id);
    setCustomerSelectError(null);
    
    try {
      logDebug(`User manually selected customer: ${customer.name}`);
      const result = await selectCustomer(customer);
      
      if (result.success) {
        logDebug('Manual customer selection successful');
        setLastActiveCustomerId(String(customer.id));
        navigate(from, { replace: true });
      } else {
        logDebug(`Manual customer selection failed: ${result.error}`);
        setCustomerSelectError(result.error || 'Failed to select customer');
      }
    } catch (error) {
      console.error('Error during customer selection:', error);
      setCustomerSelectError('Error during customer selection');
    } finally {
      setIsLoadingCustomer(false);
      setLoadingCustomerId(null);
    }
  };

  // Handle logout
  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login', { replace: true });
    } catch (error) {
      console.error('Error during logout:', error);
    }
  };

  // Show error state if there's an error
  if (customerSelectError) {
    return (
      <CustomerErrorState 
        error={customerSelectError}
        onRetry={() => {
          setCustomerSelectError(null);
          setShowSelectionUI(false);
          autoSelectionAttemptedRef.current = false;
        }}
        onLogout={handleLogout}
      />
    );
  }

  // Show no customers state if no customers are available
  if (!customers || customers.length === 0) {
    return <NoCustomersState onLogout={handleLogout} />;
  }

  // Show customer selection UI
  return (
    <div className="min-h-screen bg-background">
      <CustomerSelectionHeader onLogout={handleLogout} />
      <CustomerSelectionGrid
        customers={customers}
        onSelectCustomer={handleCustomerSelect}
        isLoading={isLoadingCustomer}
        loadingCustomerId={loadingCustomerId}
      />
    </div>
  );
};